/* eslint-disable react/prop-types */
import {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Paper,
    Stack,
    styled,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import FormStructure from './FormStructure';
import PrivateFormSettings from './PrivateFormSettings/PrivateFormSettings';
import ApprovalSettings from './PrivateFormSettings/ApprovalSettings';
import PropTypes from 'prop-types';
import {FORM_ACCESS_LEVELS} from '../../../constants/formAccessLevels';
import PublicFormSettings from './PublicFormSettings';
import {
    useCreateFormMutation,
    useLazyGetFormQuery,
    useUpdateFormMutation,
    useUpdateFormStatusMutation
} from '../../../api/services/formsService';
import useNotification from '../../../utility/hooks/useNotification';
import {FORM_STATUSES} from '../../../constants/formStatuses';
import {useGetFormCategoriesQuery} from '../../../api/services/formCategoriesService';
import useUser from '../../../utility/hooks/useUser';
import FORM_AUDIENCE_TYPE from '../../../constants/FormAudienceType';
import FormTemplateType from '../../../constants/FormTemplateType';
import {
    useCreateExternalUserFormAssignmentsMutation,
    useCreateFormEmployeeAssignmentMutation,
    useCreateFormQuestionAssignmentMutation,
    useCreateFormStudentAssignmentMutation,
    useDeleteExternalUserFormAssignmentsMutation,
    useDeleteFormAssignmentsMutation,
    useDeleteFormQuestionAssignmentMutation,
    useDeleteFormStudentsAssignmentsMutation
} from '../../../api/services/formAssignmentsService';
import {useConfirm} from 'material-ui-confirm';
import Tooltip from '@mui/material/Tooltip';
import FORM_ASSIGNMENT_TYPE from '../../../constants/FormAssignmentType';
import AutoresponderSettings from './PrivateFormSettings/AutoresponderSettings';
import FormAccessSettings from './FormAccessSettings';
import AdmicityTabs from '../../../shared-components/AdmicityTabs';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {GoLive} from './FormGoLive';

const audienceOptionsByFormTemplateType =
    {
        transition: [{
            value: FORM_AUDIENCE_TYPE.previousSchool,
            label: 'Previous School'
        }],
        offer: [{
            value: FORM_AUDIENCE_TYPE.parentsForChildCompletion,
            label: 'Parents'
        }],
        default: [
            {
                value: FORM_AUDIENCE_TYPE.parentsForChildCompletion,
                label: 'Parents'
            },
            {
                value: FORM_AUDIENCE_TYPE.externalUsers,
                label: 'External Users'
            },
            {
                value: FORM_AUDIENCE_TYPE.students,
                label: 'Students'
            },
            {
                value: FORM_AUDIENCE_TYPE.employees,
                label: 'Employees'
            }
        ]
    };

const FormEditor = ({
                        mode,
                        formId: initialFormId,
                        isWizardMode,
                        schema: externalSchema,
                        onSchemaChange: externalOnSchemaChange,
                        onSchemaValidationChange: externalOnSchemaValidationChange,
                    }) => {
    const isEditMode = mode === 'edit';
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [formId, setFormId] = useState(() =>
        isWizardMode ? initialFormId : params.id
    );
    const confirm = useConfirm();
    const {
        showErrorNotification,
        showSuccessNotification,
        showDetailedErrorNotification,
        showInfoNotification
    } = useNotification();

    const [schema, setSchema] = isWizardMode
        ? [
            externalSchema,
            externalOnSchemaChange
        ]
        : useState(undefined);

    const [schemaValidation, setSchemaValidation] = useState({});
    const handleSchemaValidationChange = (nextValidation) => {
        setSchemaValidation(nextValidation);

        if (typeof externalOnSchemaValidationChange === 'function') {
            externalOnSchemaValidationChange(nextValidation);
        }
    };

    const [componentToEditIndex, setComponentToEditIndex] = useState(undefined);
    const [formStatus, setFormStatus] = useState(undefined);
    const [template, setTemplate] = useState(undefined);
    const [accessLevel, setAccessLevel] = useState();
    const [expirationDate, setExpirationDate] = useState();
    const [remindInterval, setRemindInterval] = useState(0);
    const [strictRecipientsFiltering, setStrictRecipientsFiltering] = useState(false);
    const [allParentsCanSubmit, setAllParentsCanSubmit] = useState(false);
    const [allowMultipleSubmissions, setAllowMultipleSubmissions] = useState(false);
    const [enableRecipientNotifications, setEnableRecipientNotifications] = useState(true);
    const [name, setName] = useState('');
    const [categories, setCategories] = useState([]);
    const [audience, setAudience] = useState(FORM_AUDIENCE_TYPE.none);
    const [notificationEmails, setNotificationEmails] = useState([]);
    const [freezeAccessLevel, setFreezeAccessLevel] = useState(false);
    const [enableAutoresponder, setEnableAutoresponder] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(false);
    const [isCommunicationForm, setIsCommunicationForm] = useState(false);
    const [privateFormSettings, setPrivateFormSettings] = useState({
        name: name ?? '',
        expirationDate: expirationDate ?? null,
        remindInterval: remindInterval ?? 0,
        categories: categories ?? [],
        groupFilters: [],
        studentFilters: [],
        applicationFilters: [],
        enquiryFilters: [],
        employeeFilters: [],
        externalUserEmailAssignments: [],
        triggerFormSettings: {
            selectedForm: null,
            selectedFormSchema: null,
            relatedComponent: null,
            targetState: null
        },
        validation: {
            isValid: true,
            error: ''
        },
        assignmentType: FORM_ASSIGNMENT_TYPE.students
    });
    const [audienceOptions, setAudienceOptions] = useState([]);
    const assignmentTypeValue = privateFormSettings.assignmentType;
    let recipientType;
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    switch (assignmentTypeValue) {
        case FORM_ASSIGNMENT_TYPE.students:
            recipientType = 'student';
            break;
        case FORM_ASSIGNMENT_TYPE.employees:
            recipientType = 'employee';
            break;
        default:
            recipientType = 'none';
    }

    const {data: formCategories} = useGetFormCategoriesQuery();
    const [saveForm, {isLoading: isFormSaving}] = isEditMode || formId != null
        ? useUpdateFormMutation()
        : useCreateFormMutation();
    const [updateFormStatus, {isLoading: isPublishingForm}] = useUpdateFormStatusMutation();
    const [getForm] = useLazyGetFormQuery();
    const [createFormStudentAssignments] = useCreateFormStudentAssignmentMutation();
    const [createFormEmployeeAssignment] = useCreateFormEmployeeAssignmentMutation();
    const [createExternalUserFormAssignments] = useCreateExternalUserFormAssignmentsMutation();
    const [deleteFormAssignments] = useDeleteFormAssignmentsMutation();
    const [deleteFormStudentsAssignments] = useDeleteFormStudentsAssignmentsMutation();
    const [deleteExternalUserFormAssignments] = useDeleteExternalUserFormAssignmentsMutation();
    const [createFormQuestionAssignment] = useCreateFormQuestionAssignmentMutation();
    const [deleteFormQuestionAssignments] = useDeleteFormQuestionAssignmentMutation();

    const formRef = useRef(null);
    const formAccessRef = useRef(null);
    const {user} = useUser();

    const schoolLogoSchemaComponent = useMemo(() => ({
        type: 'schoolLogo',
        url: user.schoolLogoUrl
    }), [user.schoolLogoUrl]);
    const navigateToFormsAfterPublish = (isCommunication) => {
        const activeTabIndex = isCommunication ? 5 : 1;

        navigate('/forms', {state: {activeTabIndex}});
    };

    const modifyAccessLevelByTemplateAndAudience = (value, templateArg, audienceArg) => {
        setAccessLevel(value);
        setFreezeAccessLevel(
            (templateArg && (
                templateArg.type === FormTemplateType.transition ||
                templateArg.type === FormTemplateType.offer ||
                templateArg.type === FormTemplateType.application ||
                templateArg.type === FormTemplateType.acceptance ||
                templateArg.type === FormTemplateType.shareable
            )) ||
            audienceArg === FORM_AUDIENCE_TYPE.students ||
            audienceArg === FORM_AUDIENCE_TYPE.externalUsers ||
            audienceArg === FORM_AUDIENCE_TYPE.employees
        );
    };
    const canSwitchToEmail = useMemo(() => {
        if (!Array.isArray(schema) || schema.length === 0) {
            return false;
        }

        const contentComponents = schema.filter(
            (component) => component?.type !== 'schoolLogo'
        );

        if (contentComponents.length !== 1) {
            return false;
        }

        const onlyComponent = contentComponents[0];

        return onlyComponent?.type === 'additionalNotes';
    }, [schema]);

    useEffect(() => {
        if (!formId) return;

        if (isEditMode || !isWizardMode) {
            getForm(formId)
                .then(({data: form}) => {
                    setFormId(form.id);
                    setSchema(form.schema);
                    setName(form.name);
                    setFormStatus(form.status);
                    setRemindInterval(form.remindInterval);
                    setExpirationDate(form.expirationDate);
                    setCategories(form.categories?.map(x => x.id) ?? []);
                    setStrictRecipientsFiltering(form.strictRecipientsFiltering);
                    setAllParentsCanSubmit(form.allParentsCanSubmit);
                    setAllowMultipleSubmissions(form.allowMultipleSubmissions);
                    setEnableRecipientNotifications(form.enableRecipientNotifications ?? true);
                    setAudience(form.audience);
                    setAccessLevel(form.accessLevel);
                    setTemplate(form.template);
                    setIsCommunicationForm(form.isCommunication);
                    setFreezeAccessLevel(
                        (form.template && (
                            form.template.type === FormTemplateType.transition ||
                            form.template.type === FormTemplateType.offer)) ||
                        form.audience === FORM_AUDIENCE_TYPE.students ||
                        form.audience === FORM_AUDIENCE_TYPE.externalUsers ||
                        form.audience === FORM_AUDIENCE_TYPE.employees
                    );
                    setNotificationEmails(form.notificationEmails?.map(email => ({email})) || []);
                    setPrivateFormSettings(prev => ({
                        ...prev,
                        assignmentType: form.assignmentType,
                        scheduledActivationAt: form.scheduledActivationAt
                    }));
                    setEnableAutoresponder(form.enableAutoresponder);
                })
                .catch(() => {
                    showErrorNotification('Failed to retrieve form data');
                });
        }
    }, [formId, isEditMode]);

    useEffect(() => {
        if (template) {
            const isTransitionForm = template.type === FormTemplateType.transition;
            const isOfferForm = template.type === FormTemplateType.offer;
            const isPublicForm = template.type === FormTemplateType.acceptance ||
                template.type === FormTemplateType.application ||
                template.type === FormTemplateType.shareable;
            const audienceOptionsLocal = isTransitionForm
                ? audienceOptionsByFormTemplateType.transition
                : isOfferForm
                    ? audienceOptionsByFormTemplateType.offer
                    : audienceOptionsByFormTemplateType.default;
            const accessLevelLocal = isTransitionForm || isOfferForm
                ? FORM_ACCESS_LEVELS.publicWithStudentLink
                : isPublicForm
                    ? FORM_ACCESS_LEVELS.public
                    : FORM_ACCESS_LEVELS.private;

            if (!isEditMode) {
                if (audienceOptionsLocal.length === 1) {
                    const audienceOption = audienceOptionsLocal[0];

                    modifyAccessLevelByTemplateAndAudience(accessLevelLocal, template, audienceOption);
                } else {
                    setAccessLevel(accessLevelLocal);
                }

                if (template?.schema) {
                    const value = [...template.schema, schoolLogoSchemaComponent];
                    setSchema(value);
                }
            }
            setAudienceOptions(audienceOptionsLocal);
        }
    }, [template?.id]);

    const handleRemindIntervalChange = (value) => {
        const intValue = parseInt(value, 10);
        if (!isNaN(intValue) && intValue >= 0 && intValue <= 1000) {
            setRemindInterval(intValue);
        } else if (value === '') {
            setRemindInterval('');
        }
    };

    const isArchived = formStatus === FORM_STATUSES.archived;
    const isExpired = formStatus === FORM_STATUSES.expired;
    const isActive = formStatus === FORM_STATUSES.active;

    const isCommunicationActive = isCommunicationForm && formStatus === FORM_STATUSES.active;

    const isReadOnlyForm = isArchived || isExpired || isCommunicationActive;

    const canSaveForm =
        (
            formStatus == null ||
            formStatus === FORM_STATUSES.inactive ||
            formStatus === FORM_STATUSES.active ||
            formStatus === FORM_STATUSES.expired ||
            formStatus === FORM_STATUSES.archived
        );

    const handleNameChange = (event) => setName(event.target.value);

    useEffect(() => {
        const ENTER_THRESHOLD = 220;
        const EXIT_THRESHOLD = 320;

        const handleScroll = () => {
            const doc = document.documentElement;
            const scrollTop = doc.scrollTop || document.body.scrollTop;
            const scrollHeight = doc.scrollHeight || document.body.scrollHeight;
            const clientHeight = doc.clientHeight;

            const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

            setIsAtBottom(prev => {
                if (!prev && distanceFromBottom < ENTER_THRESHOLD) {
                    return true;
                }

                if (prev && distanceFromBottom > EXIT_THRESHOLD) {
                    return false;
                }

                return prev;
            });
        };

        window.addEventListener('scroll', handleScroll, {passive: true});

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const saveFormAndAccessSettings = async (skipAssignmentsSave = false) => {
        await handleSaveForm(skipAssignmentsSave);

        if (formAccessRef.current) {
            try {
                await formAccessRef.current.saveAccessSettings();
            } catch {

            }
        }
    };

    const getFromTabIndex = () =>
        typeof location.state?.fromTabIndex === 'number'
            ? location.state.fromTabIndex
            : 1;

    const handleSaveAndExit = () => {
        const fromTabIndex = getFromTabIndex();

        confirmSave(async () => {
            await saveFormAndAccessSettings();
            navigate('/forms', {state: {activeTabIndex: fromTabIndex}});
        });
    };

    const handleExitWithoutSave = () => {
        const fromTabIndex = getFromTabIndex();
        navigate('/forms', {state: {activeTabIndex: fromTabIndex}});
    };

    const handleSaveForm = async (
        skipAssignmentsSave = false,
        isCommunicationOverride
    ) => {
        setComponentToEditIndex(-1);

        try {
            const isCommunicationValue =
                typeof isCommunicationOverride === 'boolean'
                    ? isCommunicationOverride
                    : isCommunicationForm;

            if (isEditMode || formId != null) {
                await saveForm({
                    id: formId,
                    schema,
                    name,
                    remindInterval,
                    categoryIds: categories,
                    strictRecipientsFiltering,
                    allParentsCanSubmit,
                    allowMultipleSubmissions,
                    enableRecipientNotifications,
                    audience,
                    accessLevel,
                    expirationDate,
                    notificationEmails: notificationEmails.map(value => value.email),
                    assignmentType: privateFormSettings.assignmentType ?? 0,
                    enableAutoresponder,
                    scheduledActivationAt: privateFormSettings.scheduledActivationAt,
                    isCommunication: isCommunicationValue,
                });
            } else {
                const formAudience = audienceOptions.length === 1
                    ? audienceOptions[0].value
                    : audience;

                const response = await saveForm({
                    templateId: template.id,
                    schema,
                    name,
                    remindInterval,
                    categoryIds: categories,
                    strictRecipientsFiltering,
                    allParentsCanSubmit,
                    allowMultipleSubmissions,
                    enableRecipientNotifications,
                    audience: formAudience,
                    accessLevel,
                    expirationDate,
                    notificationEmails: notificationEmails.map(value => value.email),
                    assignmentType: privateFormSettings.assignmentType,
                    scheduledActivationAt: privateFormSettings.scheduledActivationAt,
                    isCommunication: isCommunicationValue,
                });

                const savedFormId = response.data?.formId;

                if (savedFormId) {
                    setFormId(savedFormId);
                    setAudience(formAudience);
                }
            }

            if (!skipAssignmentsSave) {
                await handleSaveFormSettings();
            }
            showSuccessNotification('Form Saved Successfully!');
        } catch (error) {
            if (error.type === 'SaveFormSettingsError') {
                showDetailedErrorNotification('Failed to save form settings', error.details);
            } else {
                showErrorNotification('Failed to save form');
            }
        }
    };

    const confirmSave = (func) => {
        setComponentToEditIndex(-1);

        confirm({
            title: 'Confirmation',
            description: 'Are you sure you want to save the changes and proceed?',
            confirmationText: 'Save',
        })
            .then(func)
            .catch(() => {
            })
    };

    const handleSaveFormSettings = async () => {
        const classifyFilters = (filters) => {
            return filters.reduce(
                (acc, filter) => {
                    filter.isDeleted && !filter.isNew
                        ? acc.deleted.push(filter)
                        : filter.isNew && acc.created.push(filter);
                    return acc;
                },
                {
                    created: [],
                    deleted: []
                }
            );
        };

        const studentFilters = classifyFilters([
            ...privateFormSettings.studentFilters,
            ...privateFormSettings.applicationFilters,
            ...privateFormSettings.enquiryFilters
        ]);
        const groupFilters = classifyFilters(privateFormSettings.groupFilters);
        const employeeFilters = classifyFilters(privateFormSettings.employeeFilters);
        const externalUserFormAssignments = classifyFilters(privateFormSettings.externalUserEmailAssignments);
        const tasks = [];
        const createdGroupFilters = groupFilters.created.length
            ? groupFilters.created.map(x => ({
                filterId: x.id,
                excluded: x.excluded
            }))
            : [];
        const personAssignments =
            studentFilters.created.length
                ? studentFilters.created.map(x => ({
                    personId: x.id,
                    name: x.label
                }))
                : employeeFilters.created.length
                    ? employeeFilters.created.map(x => ({
                        personId: x.id,
                        name: x.label || `${x.preferredForename} ${x.preferredSurname}`
                    }))
                    : [];

        if (personAssignments.length || createdGroupFilters.length) {
            const func = {
                [FORM_ASSIGNMENT_TYPE.students]: createFormStudentAssignments,
                [FORM_ASSIGNMENT_TYPE.employees]: createFormEmployeeAssignment,
            }[privateFormSettings.assignmentType] ?? (() => {
            });

            tasks.push({
                action: () => func({
                    formId,
                    personAssignments,
                    groupFilters: createdGroupFilters
                }),
                label: 'creating person + group filters'
            });
        }

        if (groupFilters.deleted.length) {
            tasks.push({
                action: () => deleteFormAssignments({
                    formId,
                    formAssignmentIds: groupFilters.deleted.map(x => x.groupId)
                }),
                label: 'deleting group filters'
            });
        }

        if (studentFilters.deleted.length) {
            tasks.push({
                action: () => deleteFormStudentsAssignments({
                    formId,
                    ids: studentFilters.deleted.map(x => x.assignmentId)
                }),
                label: 'deleting student filters'
            });
        }

        if (externalUserFormAssignments.deleted.length) {
            tasks.push({
                action: () => deleteExternalUserFormAssignments({
                    formId,
                    assignmentIds: externalUserFormAssignments.deleted.map(x => x.id)
                }),
                label: 'deleting external user form assignments'
            });
        }

        if (externalUserFormAssignments.created.length) {
            tasks.push({
                action: () => createExternalUserFormAssignments({
                    formId,
                    emails: externalUserFormAssignments.created.map(x => x.email)
                }),
                label: 'creating external user form assignments'
            });
        }

        if (employeeFilters.deleted.length) {
            tasks.push({
                action: () => deleteFormStudentsAssignments({
                    formId,
                    ids: employeeFilters.deleted.map(x => x.assignmentId)
                }),
                label: 'deleting employee filters'
            });
        }

        if (privateFormSettings.triggerFormSettings) {
            const {
                selectedForm,
                original,
                relatedComponent,
                targetState
            } = privateFormSettings.triggerFormSettings;

            if (!selectedForm && original) {
                tasks.push({
                    action: () => deleteFormQuestionAssignments({
                        id: original.id,
                        formId
                    }),
                    label: 'deleting form trigger'
                });
            }

            if (selectedForm && (
                original == null ||
                selectedForm.id !== original.selectedForm.id ||
                relatedComponent.id !== original.relatedComponent.id ||
                targetState !== original.targetState
            )) {
                tasks.push({
                    action: () => createFormQuestionAssignment({
                        formId,
                        targetState: typeof targetState === 'string' ? targetState : `${targetState}`,
                        relatedComponentId: relatedComponent.id,
                        relatedFormId: selectedForm.id,
                        displayName: `${selectedForm.name} - ${relatedComponent.description}`,
                        emails: externalUserFormAssignments.created.map(x => x.email)
                    }),
                    label: 'creating form trigger'
                });
            }
        }

        if (!tasks.length) return;

        const errors = [];

        for (const task of tasks) {
            const result = await task.action();

            if (result.error) {
                const reason = result.error.data?.validationErrors?.map(x => x.errorMessage).join('; ');

                errors.push({
                    errorMessage: `Error while ${task.label}. ${reason}`
                });
            }
        }

        if (errors.length) {
            const error = new Error('Saving failed');

            error.type = 'SaveFormSettingsError';
            error.details = errors;

            throw error;
        }
    };

    const isFormIncomplete = () => !schema ||
        Object.values(schemaValidation).filter(Boolean).length !== 0 ||
        !accessLevel ||
        !name ||
        remindInterval === '' ||
        !privateFormSettings.validation.isValid;

    const getFormValidationMessage = () => {
        if (!name) {
            return 'Please enter form name';
        }

        if (schemaValidation) {
            return Object.values(schemaValidation).filter(Boolean).join('; ');
        }

        if (!privateFormSettings.validation.isValid) {
            return privateFormSettings.validation.error;
        }

        return null;
    };

    const handleCategoriesChange = value => setCategories(value.target.value);

    const handleSetAudience = (value) => {
        const newAccessLevelsByAudience = {
            [FORM_AUDIENCE_TYPE.externalUsers]: FORM_ACCESS_LEVELS.publicWithStudentLink,
            [FORM_AUDIENCE_TYPE.students]: FORM_ACCESS_LEVELS.private,
            [FORM_AUDIENCE_TYPE.employees]: FORM_ACCESS_LEVELS.private,
            default: accessLevel
        };
        const newAccessLevel = newAccessLevelsByAudience[value] || newAccessLevelsByAudience.default;
        const newAssignmentType = value === FORM_AUDIENCE_TYPE.employees
            ? FORM_ASSIGNMENT_TYPE.employees
            : FORM_ASSIGNMENT_TYPE.students;

        setAudience(value);
        setAccessLevel(newAccessLevel);
        setFreezeAccessLevel(
            (template && (
                template.type === FormTemplateType.transition ||
                template.type === FormTemplateType.offer)) ||
            value === FORM_AUDIENCE_TYPE.students ||
            value === FORM_AUDIENCE_TYPE.externalUsers ||
            value === FORM_AUDIENCE_TYPE.employees
        );
        setPrivateFormSettings(prev => ({
            ...prev,
            assignmentType: newAssignmentType
        }));
    };

    const hasRecipients = useMemo(() => {
        const s = privateFormSettings;
        const hasGroups = s.groupFilters?.some(x => !x.isDeleted && !x.deleted);
        const hasStudents = [
            ...(s.studentFilters || []),
            ...(s.applicationFilters || []),
            ...(s.enquiryFilters || []),
        ].some(x => !x.isDeleted && !x.deleted);
        const hasEmployees = (s.employeeFilters || []).some(x => !x.isDeleted && !x.deleted);
        const hasExternal = (s.externalUserEmailAssignments || []).some(x => !x.isDeleted && !x.deleted);

        return !!(hasGroups || hasStudents || hasEmployees || hasExternal);
    }, [privateFormSettings]);

    const handleGoLiveNow = async () => {
        if (!formId) {
            showErrorNotification('Form must be saved before it can go live');
            return;
        }

        try {
            await saveFormAndAccessSettings();
            const result = await updateFormStatus({id: formId});

            if (result.error) {
                showErrorNotification(result.error.data);
                return;
            }

            if (isCommunicationForm) {
                showInfoNotification('Communication sent');
            } else {
                showInfoNotification('Form is now live');
            }

            navigateToFormsAfterPublish(isCommunicationForm);
        } catch (error) {
            showErrorNotification('Failed to publish form');
        }
    };

    const handleScheduleGoLive = async (id, isoDate) => {
        if (isoDate == null) {
            setPrivateFormSettings(prev => ({
                ...prev,
                scheduledActivationAt: null,
            }));
            return;
        }

        try {
            await saveForm({
                id,
                schema,
                name,
                remindInterval,
                categoryIds: categories,
                strictRecipientsFiltering,
                allParentsCanSubmit,
                allowMultipleSubmissions,
                enableRecipientNotifications,
                audience,
                accessLevel,
                expirationDate,
                notificationEmails: notificationEmails.map(value => value.email),
                assignmentType: privateFormSettings.assignmentType ?? 0,
                enableAutoresponder,
                scheduledActivationAt: isoDate,
            });

            setPrivateFormSettings(prev => ({
                ...prev,
                scheduledActivationAt: isoDate,
            }));

            showSuccessNotification('Live date scheduled');
        } catch {
            showErrorNotification('Failed to schedule live date');
        }
    };
    const handleScheduledActivationChange = (isoDate) => {
        setPrivateFormSettings(prev => ({
            ...prev,
            scheduledActivationAt: isoDate,
        }));
    };

    const handleGoLiveLater = async () => {
        const fromTabIndex = getFromTabIndex();

        try {
            await saveFormAndAccessSettings();
            navigate('/forms', {state: {activeTabIndex: fromTabIndex}});
        } catch (err) {
            showErrorNotification('Failed to save form before exiting');
        }
    };
    const handleCommunicationTypeChange = async (isEmailCommunication) => {

        setIsCommunicationForm(isEmailCommunication);

        try {

            await handleSaveForm(true, isEmailCommunication);

            showInfoNotification('Communication type updated');
        } catch {
            showErrorNotification('Failed to update communication type');
        }
    };

    const tabs = [
        {
            label: 'Form Builder',
            component: <FormStructure
                formRef={formRef}
                isWizardMode={isWizardMode}
                accessLevel={accessLevel}
                name={name}
                schema={schema}
                templateId={template?.id}
                formCategories={formCategories}
                onNameChange={handleNameChange}
                onSchemaChange={setSchema}
                onSchemaValidationChange={handleSchemaValidationChange}
                editMode
                expirationDate={expirationDate}
                remindInterval={remindInterval}
                onRemindIntervalChange={handleRemindIntervalChange}
                onExpirationDateChange={setExpirationDate}
                isFormArchived={isReadOnlyForm}
                isCommunication={isCommunicationForm}
                formId={formId}
                categories={categories}
                onCategoriesChange={handleCategoriesChange}
                componentToEditIndex={componentToEditIndex}
                setComponentToEditIndex={setComponentToEditIndex}
                audience={audience}
                recipientType={recipientType}
            />
        },
        {
            label: 'Recipients & Settings',
            component: accessLevel === FORM_ACCESS_LEVELS.private ||
            accessLevel === FORM_ACCESS_LEVELS.publicWithStudentLink
                ? <PrivateFormSettings
                    formId={formId}
                    name={name}
                    expirationDate={expirationDate}
                    onExpirationDateChange={setExpirationDate}
                    remindInterval={remindInterval}
                    categories={categories}
                    onNameChange={setName}
                    onRemindIntervalChange={setRemindInterval}
                    onCategoriesChange={setCategories}
                    formCategories={formCategories}
                    isFormLive={formStatus === 'Active'}
                    isFormArchived={isReadOnlyForm}
                    strictRecipientsFiltering={strictRecipientsFiltering}
                    audience={audience}
                    audienceOptions={audienceOptions}
                    allParentsCanSubmit={allParentsCanSubmit}
                    allowMultipleSubmissions={allowMultipleSubmissions}
                    enableRecipientNotifications={enableRecipientNotifications}
                    accessLevel={accessLevel}
                    disableAccessLevel={freezeAccessLevel}
                    notificationEmails={notificationEmails}
                    onSetStrictRecipientsFiltering={setStrictRecipientsFiltering}
                    onAudienceChange={handleSetAudience}
                    settings={privateFormSettings}
                    onSettingsChange={setPrivateFormSettings}
                    onAllParentsCanSubmitChange={setAllParentsCanSubmit}
                    onAllowMultipleSubmissionsChange={setAllowMultipleSubmissions}
                    onEnableRecipientNotificationsChange={setEnableRecipientNotifications}
                    onAccessLevelChange={setAccessLevel}
                    onNotificationEmailsChange={setNotificationEmails}
                    isCommunicationForm={isCommunicationForm}
                    onCommunicationTypeChange={handleCommunicationTypeChange}
                    canSwitchToEmail={canSwitchToEmail}
                />
                : <PublicFormSettings
                    formId={formId}
                    name={name}
                    onNameChange={setName}
                    formCategories={formCategories}
                    categories={categories}
                    onCategoriesChange={setCategories}
                    notificationEmails={notificationEmails}
                    onNotificationEmailsChange={setNotificationEmails}
                    expirationDate={expirationDate}
                    onExpirationDateChange={setExpirationDate}
                    isFormArchived={isReadOnlyForm}
                />
        },
        {
            label: 'Form Access',
            component: <FormAccessSettings
                ref={formAccessRef}
                formId={formId}
                formName={name}
            />
        },
        ...((accessLevel && accessLevel !== FORM_ACCESS_LEVELS.public) && !isCommunicationForm
            ? [
                {
                    label: 'Form Approvals',
                    component: <ApprovalSettings
                        formId={formId}
                        isFormLive={formStatus === 'Active'}
                        isFormPrivate={accessLevel === FORM_ACCESS_LEVELS.private}
                        isFormArchived={isReadOnlyForm}
                    />
                }
            ]
            : []),
        ...(!isCommunicationForm
            ? [
        {
            label: 'Automatic Responses',
            component: <AutoresponderSettings
                formId={formId}
                schema={schema}
                enableAutoresponder={enableAutoresponder}
                onAutoresponderToggle={setEnableAutoresponder}
                accessLevel={accessLevel}
                isFormArchived={isReadOnlyForm}
            />
        }]
            : []),
        ...(formStatus === FORM_STATUSES.inactive && formId
            ? [
                {
                    label: 'Go Live',
                    component: (
                        <GoLive
                            formId={formId}
                            formType={template?.type}
                            hasRecipients={hasRecipients}
                            onMakeLive={handleGoLiveNow}
                            onSchedule={handleScheduleGoLive}
                            onLater={handleGoLiveLater}
                            formName={name}
                            expirationDate={expirationDate}
                            scheduledActivationAt={privateFormSettings.scheduledActivationAt}
                            onScheduledActivationChange={handleScheduledActivationChange}
                        />
                    )
                }
            ]
            : []),
    ];

    const getBackdropMessage = () => {
        if (isFormSaving) return 'Saving form';
        if (isPublishingForm) return 'Publishing form';

        return '';
    };
    const StickyFooter = styled('div')(({theme}) => ({
        position: 'sticky',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: theme.palette.background.paper,
        borderTop: '1px solid #e0e0e0',
        padding: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
        zIndex: 999,
    }));
    const renderActionButtons = (isFixedBar) => {
        if (!canSaveForm) {
            return (
                <Button
                    variant="outlined"
                    onClick={handleExitWithoutSave}
                >
                    Back to Forms
                </Button>
            );
        }

        const useBackToForms = isArchived || isExpired || isActive;

        return (
            <>
                {useBackToForms
                    ? (
                        <Button
                            variant="outlined"
                            onClick={handleExitWithoutSave}
                        >
                            Back to Forms
                        </Button>
                    )
                    : (
                        <Button
                            variant="outlined"
                            disabled={isFormIncomplete() || isFormSaving || isPublishingForm}
                            onClick={handleSaveAndExit}
                        >
                            Save and exit
                        </Button>
                    )}

                <Box sx={{ml: 'auto'}}>
                    <Tooltip title={getFormValidationMessage()}>
                        <span>
                            <Button
                                variant="contained"
                                sx={{
                                    boxShadow: 'none',
                                    mr: isFixedBar ? 4 : 0,
                                    '&:hover': {
                                        backgroundColor: '#155FA7',
                                        boxShadow: 'none',
                                    },
                                }}
                                disabled={isFormIncomplete() || isFormSaving || isPublishingForm}
                                onClick={() =>
                                    confirmSave(async () => {
                                        await saveFormAndAccessSettings();
                                    })
                                }
                            >
                                Save form
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </>
        );
    };

    return (
        <>
            <Box
                sx={{
                    overflowX: 'auto',
                    maxWidth: '100%',
                    '&::-webkit-scrollbar': {height: '4px'},
                    '&::-webkit-scrollbar-track': {backgroundColor: '#f1f1f1'},
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#c1c1c1',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {backgroundColor: '#a1a1a1'},
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#c1c1c1 #f1f1f1',
                }}
            >
                <Paper
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minHeight: isWizardMode ? 0 : '100%',
                        minWidth: '1000px',
                    }}
                    variant={isWizardMode ? undefined : 'outlined'}
                >
                    {!isWizardMode && isEditMode && (
                        <>
                            <AdmicityTabs tabs={tabs} displayDivider/>

                            {(isSmallScreen || isAtBottom) && (
                                <StickyFooter>
                                    {renderActionButtons(false)}
                                </StickyFooter>
                            )}
                        </>
                    )}

                    {!isWizardMode && isEditMode && !isSmallScreen && !isAtBottom && (
                        <Box
                            sx={(theme) => ({
                                position: 'fixed',
                                bottom: theme.spacing(2),
                                left: theme.spacing(2),
                                right: theme.spacing(2),
                                zIndex: theme.zIndex.drawer + 2,
                                pointerEvents: 'none',
                            })}
                        >
                            <Box
                                sx={(theme) => ({
                                    pointerEvents: 'auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    padding: theme.spacing(2),
                                    backgroundColor: 'transparent',
                                })}
                            >
                                {renderActionButtons(true)}
                            </Box>
                        </Box>
                    )}

                    {isWizardMode && tabs[0].component}

                    <Backdrop
                        sx={{
                            color: '#fff',
                            zIndex: (theme) => theme.zIndex.drawer + 1,
                        }}
                        open={isFormSaving || isPublishingForm}
                    >
                        <Stack flexDirection="column" alignItems="center">
                            <CircularProgress size={40}/>
                            <Typography>{getBackdropMessage()}</Typography>
                        </Stack>
                    </Backdrop>
                </Paper>
            </Box>
        </>
    );

};

FormEditor.propTypes = {
    mode: PropTypes.string.isRequired,
    wizard: PropTypes.object,
    setWizard: PropTypes.func,
    isWizardMode: PropTypes.bool,
    formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    forceFormId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSchemaValidationChange: PropTypes.func,
};

export default FormEditor;
